namespace $.$$ {

	type $shm_projects_timeline_digests = {
		days: $shm_projects_digest_record[]
		week: $shm_projects_digest_record | null
		enabled: boolean
		timezone: string
		status?: { error?: string }
	}

	function $shm_projects_timeline_digest_record( value: unknown ): $shm_projects_digest_record | null {
		if( !value || typeof value !== 'object' ) return null
		const record = value as Partial< $shm_projects_digest_record >
		return typeof record.start === 'string' && typeof record.kind === 'string'
			? record as $shm_projects_digest_record
			: null
	}

	export function $shm_projects_timeline_digests_normalize( value: unknown ): $shm_projects_timeline_digests {
		const data = value && typeof value === 'object'
			? value as Partial< $shm_projects_timeline_digests >
			: {}
		const days = Array.isArray( data.days )
			? data.days.map( $shm_projects_timeline_digest_record ).filter(( record ): record is $shm_projects_digest_record => record !== null )
			: []
		const week = $shm_projects_timeline_digest_record( data.week )
		return {
			days,
			week,
			enabled: data.enabled === true,
			timezone: typeof data.timezone === 'string' ? data.timezone : 'Europe/Moscow',
			status: data.status,
		}
	}

	type $shm_projects_timeline_event = {
		project: $shm_projects_data_record
		version: $shm_projects_data_version
		count: number
	}

	export class $shm_projects_timeline extends $.$shm_projects_timeline {
		@ $mol_mem
		digests( next?: $shm_projects_timeline_digests ) {
			const revision = Math.floor( this.$.$mol_state_time.now( 60_000 ) / 60_000 )
			if( next ) return next
			const url = new URL( this.$.$shm_projects_config.projects_api_url() )
			url.searchParams.set( 'digests', this.timeline_days()[0] )
			url.searchParams.set( 'revision', String( revision ) )
			const token = this.$.$shm_projects_config.agent_token()
			return $shm_projects_timeline_digests_normalize(
				this.$.$mol_fetch.json( url.toString(), { cache: 'no-store', headers: token ? { 'X-Hitalama-Agent-Token': token } : {} } ),
			)
		}
		week_summary() { return this.digests().week }
		day_summary_rows( day: string ) {
			const today = new Intl.DateTimeFormat( 'en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' } ).format( new Date( this.$.$mol_state_time.now( 60_000 ) ) )
			return day <= today ? [ this.Day_summary( day ) ] : []
		}
		day_summary( day: string ) {
			const digest = this.digests().days.find( item => item.start === day )
			return digest?.content ?? digest?.notes?.technical ?? ''
		}
		@ $mol_action
		refresh_day( day: string ) {
			const record = this.$.$mol_wire_sync( this.$.$shm_projects_digest_api ).refresh( 'day', day )
			const data = this.digests()
			this.digests({ ...data, days: [ ...data.days.filter( item => item.start !== record.start ), record ] })
		}
		@ $mol_action
		refresh_week() {
			const record = this.$.$mol_wire_sync( this.$.$shm_projects_digest_api ).refresh( 'week', this.timeline_days()[0] )
			this.digests({ ...this.digests(), week: record })
		}

		week_select_pointerdown( event?: PointerEvent | null ) {
			event?.stopPropagation()
			return event
		}

		projects() {
			return this.$.$shm_projects_data.records()
				.filter( project => !project.archived )
				.filter( project => this.$.$shm_projects_access_view( project.id ) )
		}

		private date_key( date: Date ) {
			const year = date.getFullYear()
			const month = String( date.getMonth() + 1 ).padStart( 2, '0' )
			const day = String( date.getDate() ).padStart( 2, '0' )
			return `${ year }-${ month }-${ day }`
		}

		private version_date_key( value: string ) {
			const ru = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec( value.trim() )
			if( ru ) return `${ ru[3] }-${ ru[2] }-${ ru[1] }`
			const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec( value.trim() )
			return iso ? `${ iso[1] }-${ iso[2] }-${ iso[3] }` : ''
		}

		private week_start( date: Date ) {
			const start = new Date( date )
			start.setHours( 12, 0, 0, 0 )
			start.setDate( start.getDate() - ( start.getDay() + 6 ) % 7 )
			return start
		}

		private current_week_start() {
			const today = new Date( this.$.$mol_state_time.now( 60_000 ) )
			return this.week_start( today )
		}

		private selected_week_start() {
			const selected = /^\d{4}-\d{2}-\d{2}$/.test( this.week() )
				? new Date( `${ this.week() }T12:00:00` )
				: this.current_week_start()
			return Number.isNaN( selected.getTime() ) ? this.current_week_start() : this.week_start( selected )
		}

		private week_key_from_value( value: string ) {
			const key = this.version_date_key( value )
			if( !key ) return ''
			const date = new Date( `${ key }T12:00:00` )
			return Number.isNaN( date.getTime() ) ? '' : this.date_key( this.week_start( date ) )
		}

		private week_label( key: string ) {
			const start = new Date( `${ key }T12:00:00` )
			const end = new Date( start )
			end.setDate( end.getDate() + 6 )
			const start_label = new Intl.DateTimeFormat( 'ru-RU', { day: 'numeric', month: 'short' } ).format( start ).replace( '.', '' )
			const end_label = new Intl.DateTimeFormat( 'ru-RU', { day: 'numeric', month: 'short', year: 'numeric' } ).format( end ).replace( ' г.', '' ).replace( '.', '' )
			return `${ start_label } — ${ end_label }`
		}

		week_options() {
			const current = this.current_week_start()
			const current_key = this.date_key( current )
			const keys = new Set< string >

			for( let offset = 1; offset < 52; ++ offset ) {
				const date = new Date( current )
				date.setDate( date.getDate() - offset * 7 )
				keys.add( this.date_key( date ) )
			}

			for( const project of this.projects() ) {
				for( const version of project.history ) {
					if( version.stage !== 'Пререлиз' ) continue
					const key = this.week_key_from_value( version.date )
					if( key && key !== current_key ) keys.add( key )
				}
			}

			return {
				'': `Текущая · ${ this.week_label( current_key ) }`,
				... Object.fromEntries(
					[ ... keys ].sort().reverse().map( key => [ key, this.week_label( key ) ] ),
				),
			}
		}

		timeline_days() {
			const start = this.selected_week_start()
			return Array.from( { length: 7 }, ( _, index )=> {
				const date = new Date( start )
				date.setDate( date.getDate() + index )
				return this.date_key( date )
			} )
		}

		timeline_day_rows() {
			return this.timeline_days().map( day => this.Timeline_day( day ) )
		}

		timeline_day_today( day: string ) {
			return day === this.date_key( new Date( this.$.$mol_state_time.now( 60_000 ) ) )
		}

		timeline_day_title( day: string ) {
			const date = new Date( `${ day }T12:00:00` )
			const weekday = new Intl.DateTimeFormat( 'ru-RU', { weekday: 'short' } ).format( date ).replace( '.', '' )
			const label = new Intl.DateTimeFormat( 'ru-RU', { day: '2-digit', month: 'short' } ).format( date ).replace( '.', '' )
			return `${ weekday }, ${ label }`
		}

		timeline_events( day: string ): readonly $shm_projects_timeline_event[] {
			return this.projects().flatMap( project => {
				const versions = project.history.filter( version =>
					version.stage === 'Пререлиз' && this.version_date_key( version.date ) === day
				)
				return versions.length ? [{ project, version: versions[0], count: versions.length }] : []
			} )
		}

		timeline_event_rows( day: string ) {
			const events = this.timeline_events( day )
			return events.length
				? events.map( event => this.Timeline_event( JSON.stringify( [ day, event.project.id ] ) ) )
				: [ this.Timeline_empty( day ) ]
		}

		timeline_event( key: string ) {
			const [ day, project_id ] = JSON.parse( key ) as [ string, string ]
			return this.timeline_events( day ).find( event => event.project.id === project_id ) ?? null
		}

		timeline_event_title( key: string ) {
			const event = this.timeline_event( key )
			if( !event ) return 'Пререлиз'
			const count = event.count > 1 ? ` · ${ event.count } пререлиза` : ''
			return `${ event.project.title } · v${ event.version.version }${ count }`
		}

		timeline_event_arg( key: string ) {
			const event = this.timeline_event( key )
			if( !event ) return {}
			return {
				section: 'projects',
				project: event.project.id,
				project_tab: 'history',
				project_version: event.version.id,
				project_version_output: 'result',
				projects_map: null,
			}
		}

	}

}
